import React, { useState, useEffect } from 'react';
import { getVideoStreamUrl } from './api';

interface VideoPreviewProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  videoId: string;
  currentTime: number;
  duration: number;
  isPlaying: boolean;
  fps: number;
  onTogglePlay: () => void;
  filename?: string;
}

export function VideoPreview({
  videoRef,
  videoId,
  currentTime,
  duration,
  isPlaying,
  fps,
  onTogglePlay,
  filename,
}: VideoPreviewProps) {
  const [streamUrl, setStreamUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    getVideoStreamUrl(videoId)
      .then((url) => {
        setStreamUrl(url);
        setIsLoading(false);
      })
      .catch((err) => {
        setError('Failed to load video: ' + err.message);
        setIsLoading(false);
      });
  }, [videoId]);

  // Format time as seconds with 2 decimal places + frame number
  const formatTimeWithFrames = (time: number): string => {
    const seconds = time.toFixed(2);
    const frame = Math.floor((time % 1) * fps);
    return `${seconds}s [${frame}]`;
  };

  if (isLoading) {
    return (
      <div className="at-video-preview">
        <div className="at-video-container">
          <div className="at-loading">
            <div className="at-spinner"></div>
            <span>Loading video...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="at-video-preview">
        <div className="at-video-container">
          <div className="at-error">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="at-video-preview">
      <div className="at-video-container" onClick={onTogglePlay}>
        <video
          ref={videoRef}
          src={streamUrl}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain'
          }}
          preload="metadata"
        />
        {!isPlaying && (
          <div className="at-play-overlay">
            <div className="at-play-button">▶</div>
          </div>
        )}
      </div>
      <div className="at-video-controls">
        <button className="at-play-btn" onClick={onTogglePlay}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        <div className="at-time-display">
          <span className="at-current-time">{formatTimeWithFrames(currentTime)}</span>
          <span className="at-time-separator"> / </span>
          <span className="at-total-time">{formatTimeWithFrames(duration)}</span>
        </div>
        <div className="at-fps-display">{fps} FPS</div>
      </div>
    </div>
  );
}
